import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
  CircularProgress,
  Divider,
} from '@mui/material'
import MicrosoftIcon from '@mui/icons-material/Microsoft'

import { RootState } from '../app/store'
import { useSSOLogin } from '../context/MsalProvider'
import { isAzureSSOConfigured } from '../config/authConfig'

const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useSelector((state: RootState) => state.auth)
  const { login, isConfigured: ssoConfigured, isInteractionInProgress } = useSSOLogin()
  const [error, setError] = useState<string | null>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const handleSSOLogin = async () => {
    setError(null)
    setIsRedirecting(true)
    
    try {
      await login()
      // Note: This will redirect to Azure AD, so the page will unload
      // The redirect back is handled by MsalProvider
    } catch (err: any) {
      console.error('[LoginPage] SSO login error:', err)
      setError(err.message || 'Failed to initiate sign in')
      setIsRedirecting(false)
    }
  }

  // Show loading state while checking auth status
  if (isLoading) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  // Check if SSO is configured
  const ssoEnabled = isAzureSSOConfigured() && ssoConfigured

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 400 }}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <img 
              src="/devon-logo-blue.webp" 
              alt="DevOn Logo" 
              style={{ width: 210, height: 80, margin: '0 auto 16px' }} 
            />
            <Typography component="h1" variant="h4" gutterBottom>
              E-Learning Center
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Sign in with your organization account
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {ssoEnabled ? (
            <>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={isRedirecting ? <CircularProgress size={20} color="inherit" /> : <MicrosoftIcon />}
                onClick={handleSSOLogin}
                disabled={isRedirecting || Boolean(isInteractionInProgress)}
                sx={{ 
                  mt: 2, 
                  mb: 2,
                  py: 1.5,
                  backgroundColor: '#2f2f2f',
                  '&:hover': {
                    backgroundColor: '#1a1a1a',
                  },
                }}
              >
                {isRedirecting ? 'Redirecting...' : 'Sign in with Microsoft'}
              </Button>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  You will be redirected to Microsoft to sign in with your organization credentials.
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Single Sign-On is not configured. Please contact your administrator.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Azure AD SSO must be configured for this application to function.
                Please ensure the following environment variables are set:
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2, color: 'text.secondary' }}>
                <li><code>VITE_AZURE_AD_TENANT_ID</code></li>
                <li><code>VITE_AZURE_AD_CLIENT_ID</code></li>
              </Box>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Need help? Contact your IT administrator
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  )
}

export default LoginPage
